import { goto } from "$app/navigation";
import { API_BASE_URL } from "$lib/config/index.js";
import { csrfStore } from "$lib/csrfStore";

import { sleep } from "./helper";

// Define types and interfaces
type HTTPMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "HEAD";
type ResponseType = "json" | "blob";

interface CSRFResponse {
    csrfToken: string;
}

interface APIError {
    error: "invalid-csrf-token" | "missing-credentials" | string;
    message?: string;
}

interface RequestOptions extends RequestInit {
    headers: HeadersInit & {
        "X-CSRFToken"?: string;
        "Content-Type"?: string;
        Accept?: string;
    };
}

// Single source of truth for CSRF token
let csrf: string | null = null;
csrfStore.subscribe((value: string) => (csrf = value));

function setCookie(name: string, value: string): void {
    document.cookie = `${name}=${value}; path=/`;
}

// Function to fetch CSRF token
export async function setCSRF(maxRetries: number = 3, backoff: number = 1000): Promise<boolean> {
    for (let i = 0; i < maxRetries; ++i) {
        const requestOptions: RequestInit = {
            method: "GET",
            redirect: "follow",
            credentials: "include",
        };

        try {
            const response = await fetch(
                `${API_BASE_URL}/accounts/get_csrf_token/`,
                requestOptions
            );
            const data = (await response.json()) as CSRFResponse;

            if (data.csrfToken) {
                // Set both the store value and the cookie immediately
                csrfStore.set(data.csrfToken);
                setCookie("csrftoken", data.csrfToken);
                csrf = data.csrfToken; // Update the local variable immediately
                return true;
            }

            console.error(`Attempt ${i}: Error fetching CSRF token:`, data);
        } catch (error) {
            console.error(`Attempt ${i}: Error fetching CSRF token:`, error);
        }

        if (i < maxRetries - 1) {
            await sleep(backoff);
            backoff *= 2; // Exponential backoff
        }
    }

    console.error("Max retries reached: CSRF token not found in response");
    return false;
}

// Function to handle response
async function handleResponse<T>(
    response: Response,
    url: string,
    data: unknown,
    type: ResponseType = "json",
    method: HTTPMethod = "POST",
    retries: number = 3,
    signal?: AbortSignal
): Promise<T | Blob | null> {
    // Don't redirect to login if we're already on the login page
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
    if ((response.status === 401 || response.status === 403) && !currentPath.includes("/login")) {
        const path = window.location.pathname + window.location.search + window.location.hash;
        const safeRedirect = path.startsWith("/") ? path : "/";
        const redirectTo = encodeURIComponent(safeRedirect);
        goto(`/login?redirect=${redirectTo}&message=Unauthorized Access. Please login to continue`);
        throw new Error("Unauthorized");
    }

    if (response.redirected && response.url === `${API_BASE_URL}/`) {
        goto("/login");
        throw new Error("Redirected to login page");
    }

    // Return null for 204 No Content
    if (response.status === 204) {
        return null;
    }

    // Check if response has content before trying to parse it
    const contentType = response.headers.get("Content-Type");
    if (!contentType) {
        return null;
    }

    if (contentType.includes("application/json")) {
        try {
            const json = (await response.json()) as T & APIError;
            if (json.error === "invalid-csrf-token") {
                const csrfIsSet = await setCSRF();
                if (csrfIsSet) {
                    return await ajax<T>(url, data, type, method, signal, retries - 1);
                }
                throw new Error("Invalid CSRF token and failed to refresh");
            }
            if (json.error === "missing-credentials") {
                goto("/login");
            }
            return json as T;
        } catch (error: unknown) {
            // Check if response parsing was aborted
            if (signal?.aborted) {
                const abortError = new Error("Request was aborted during response processing");
                abortError.name = "AbortError";
                throw abortError;
            }
            throw error;
        }
    }

    try {
        return await response.blob();
    } catch (error: unknown) {
        // Check if blob conversion was aborted
        if (signal?.aborted) {
            const abortError = new Error("Request was aborted during blob processing");
            abortError.name = "AbortError";
            throw abortError;
        }
        throw error;
    }
}

/**
 * Function that determines if data contains numeric arrays, e.g. {primary_id, secondary_ids}
 * We can use a very simple check for keys we expect in a "merge" scenario or for numeric arrays.
 */
function shouldSendAsJson(data: any) {
    // If data contains an array of objects, send as JSON
    if (data && typeof data === "object") {
        // Check for arrays of objects or primitive values
        for (const key in data) {
            if (
                Array.isArray(data[key]) &&
                data[key].length > 0 &&
                (typeof data[key][0] === "object" || typeof data[key][0] === "number")
            ) {
                return true;
            }
        }
        // Original checks
        if ("primary_id" in data || "secondary_ids" in data || "ids" in data) {
            return true;
        }
    }
    return false;
}

/**
 * Main AJAX function that supports request cancellation through AbortController.
 *
 * Usage example with AbortController:
 * ```
 * const controller = new AbortController();
 * const { signal } = controller;
 *
 * // To cancel the request later:
 * // controller.abort();
 *
 * try {
 *   const response = await ajax('/api/endpoint', data, 'json', 'GET', signal);
 *   // Process response
 * } catch (error) {
 *   if (error.name === 'AbortError') {
 *     console.log('Request was cancelled');
 *   } else {
 *     console.error('Request failed:', error);
 *   }
 * }
 * ```
 */
export async function ajax<T>(
    url: string,
    data?: unknown,
    type: ResponseType = "json",
    method: HTTPMethod = "POST",
    signal?: AbortSignal,
    retries: number = 3
): Promise<T | Blob | null> {
    if (!csrf && !(await setCSRF())) {
        throw new Error("CSRF token is not set. We tried to initialize it but failed.");
    }

    // Check if request is already aborted
    if (signal?.aborted) {
        const abortError = new Error("Request was aborted before it started");
        abortError.name = "AbortError";
        throw abortError;
    }

    let finalUrl = url;
    method = method.toUpperCase() as HTTPMethod;

    const param: RequestOptions = {
        method: method,
        headers: {
            "X-CSRFToken": csrf ?? "",
            // We'll always accept application/json
            Accept: "application/json",
        },
        credentials: "include",
        mode: "cors",
        signal,
    };

    // Handle data based on method
    if (data && !["GET", "HEAD"].includes(method)) {
        // Handle FormData for any method
        if (data instanceof FormData) {
            // Do not set Content-Type header for FormData - browser will set it with boundary
            delete param.headers["Content-Type"];
            param.body = data;
        }
        // Force JSON if our custom logic says so or for non-FormData PUT/PATCH/DELETE requests
        else if (
            (method === "POST" && shouldSendAsJson(data)) ||
            ["PUT", "PATCH", "DELETE"].includes(method)
        ) {
            param.headers["Content-Type"] = "application/json";
            param.body = JSON.stringify(data);
        }
        // Default POST handling (convert to FormData)
        else if (method === "POST") {
            // Convert data to FormData
            const formData = new FormData();
            Object.entries(data as Record<string, any>).forEach(([key, value]) => {
                formData.append(key, value);
            });
            param.body = formData;
        }
    }

    // For GET requests, append data to URL if provided
    if (method === "GET" && data) {
        const urlParams = new URLSearchParams(data as Record<string, string>);
        finalUrl += `?${urlParams.toString()}`;
    }

    if (typeof document !== "undefined" && document && csrf) {
        setCookie("csrftoken", csrf);
    }

    try {
        const response = await fetch(finalUrl, param);

        if (response.status === 204) {
            return null;
        }

        return await handleResponse<T>(response, finalUrl, data, type, method, retries - 1, signal);
    } catch (error) {
        // Properly handle aborted requests
        if (error instanceof DOMException && error.name === "AbortError") {
            console.warn("Request aborted:", finalUrl);
            // Create a clean AbortError to throw
            const abortError = new Error("Request was aborted");
            abortError.name = "AbortError";
            throw abortError;
        }
        throw error;
    }
}

interface StreamChunkHandler<T> {
    (chunk: T): void;
}

interface ErrorHandler {
    (error: Error): void;
}

async function handleStreamResponse<T>(
    response: Response,
    url: string,
    data: unknown,
    onChunk: StreamChunkHandler<T>,
    retries: number = 3,
    signal?: AbortSignal
): Promise<void> {
    if (response.redirected && response.url === `${API_BASE_URL}/`) {
        goto("/login");
        throw new Error("Redirected to login page");
    }

    if (response.status === 204) {
        return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error("Response body is not readable");
    }

    const decoder = new TextDecoder();

    try {
        while (true) {
            // Check if aborted before reading next chunk
            if (signal?.aborted) {
                const abortError = new Error("Stream reading was aborted");
                abortError.name = "AbortError";
                throw abortError;
            }

            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split("\n");

            for (const line of lines) {
                if (line.startsWith("data: ")) {
                    const content = line.slice(6);
                    if (content === "[DONE]") {
                        return;
                    }
                    try {
                        const jsonChunk = JSON.parse(content) as T & APIError;
                        if (jsonChunk.error === "invalid-csrf-token") {
                            const csrfIsSet = await setCSRF();
                            if (csrfIsSet) {
                                await streamingAjax(
                                    url,
                                    data,
                                    onChunk,
                                    () => {},
                                    signal,
                                    retries - 1
                                );
                                return;
                            }
                            throw new Error("Invalid CSRF token and failed to refresh");
                        }
                        if (jsonChunk.error === "missing-credentials") {
                            goto("/login");
                            return;
                        }
                        onChunk(jsonChunk);
                    } catch (error: unknown) {
                        // Only pass parsing errors to the client, not AbortErrors
                        if (error instanceof Error && error.name !== "AbortError") {
                            // If we can't parse JSON, treat content as plain text
                            onChunk(content as T);
                        } else if (error instanceof Error) {
                            throw error;
                        } else {
                            throw new Error(String(error));
                        }
                    }
                }
            }
        }
    } catch (error: unknown) {
        // Clean up the reader if we're aborting
        if (error instanceof Error && error.name === "AbortError") {
            reader.cancel().catch(console.error);
        }
        throw error;
    }
}

/**
 * Streaming AJAX function that supports request cancellation through AbortController.
 *
 * Usage example with AbortController:
 * ```
 * const controller = new AbortController();
 * const { signal } = controller;
 *
 * // To cancel the request later:
 * // controller.abort();
 *
 * try {
 *   await streamingAjax(
 *     '/api/streaming-endpoint',
 *     data,
 *     (chunk) => console.log('Received chunk:', chunk),
 *     (error) => console.error('Streaming error:', error),
 *     signal
 *   );
 * } catch (error) {
 *   if (error.name === 'AbortError') {
 *     console.log('Streaming request was cancelled');
 *   } else {
 *     console.error('Streaming request failed:', error);
 *   }
 * }
 * ```
 */
export async function streamingAjax<T>(
    url: string,
    data: unknown | null,
    onChunk: StreamChunkHandler<T>,
    onError: ErrorHandler,
    signal?: AbortSignal,
    retries: number = 3
): Promise<void> {
    // Check if request is already aborted
    if (signal?.aborted) {
        const abortError = new Error("Streaming request was aborted before it started");
        abortError.name = "AbortError";
        throw abortError;
    }

    if (!csrf && !(await setCSRF())) {
        throw new Error("CSRF token is not set. We tried to initialize it but failed.");
    }

    const param: RequestOptions = {
        method: data ? "POST" : "GET",
        headers: {
            "X-CSRFToken": csrf ?? "",
            "Content-Type": "application/json",
        },
        credentials: "include",
        signal, // Add the abort signal to the request
    };

    if (data) {
        param.body = JSON.stringify(data);
    }

    if (typeof document !== "undefined" && document && csrf) {
        setCookie("csrftoken", csrf);
    }

    try {
        const response = await fetch(url, param);
        await handleStreamResponse<T>(response, url, data, onChunk, retries, signal);
    } catch (error: unknown) {
        // Handle AbortError separately
        if (error instanceof DOMException && error.name === "AbortError") {
            console.warn("Streaming request aborted:", url);
            const abortError = new Error("Streaming request was aborted");
            abortError.name = "AbortError";
            throw abortError;
        }

        console.error("Streaming error:", error);
        onError(error instanceof Error ? error : new Error(String(error)));

        if (retries > 0 && (!(error instanceof Error) || error.name !== "AbortError")) {
            console.log(`Retrying streaming... (${retries} attempts left)`);
            await streamingAjax<T>(url, data, onChunk, onError, signal, retries - 1);
        } else {
            throw error instanceof Error ? error : new Error(String(error));
        }
    }
}
